import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RECUR_TYPES, NTH_OPTIONS, describeRecurrence } from "./calUtils";

const Pick = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Select value={String(value)} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-72">{options.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const Num = ({ label, value, onChange, testid, placeholder }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input type="number" className="font-mono-lf" value={value ?? ""} placeholder={placeholder} data-testid={testid}
      onChange={(e) => onChange(e.target.value === "" ? null : parseInt(e.target.value, 10))} />
  </div>
);

export const RecurrenceFields = ({ value: r, onChange, calendar }) => {
  const set = (k) => (v) => onChange({ ...r, [k]: v });
  const months = (calendar.month_names || []).map((n, i) => [i, n]);
  if (r.type === "computed") return <p className="text-sm text-[#A8A090]" data-testid="recurrence-computed">{describeRecurrence(r, calendar)} — the date is calculated each year.</p>;
  const types = RECUR_TYPES.filter(([t]) => (t !== "moon_phase" || calendar.moons?.length) && (t !== "nth_weekday" || calendar.weekday_names?.length));
  return (
    <div className="space-y-3 rounded-md border border-[#2D3442] p-3" data-testid="recurrence-fields">
      <Pick label="Repeats" value={r.type} onChange={set("type")} options={types} testid="recurrence-type-select" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {["yearly", "once", "nth_weekday", "every_n_days"].includes(r.type) && <Pick label="Month" value={r.month} onChange={(v) => set("month")(parseInt(v, 10))} options={months} testid="recurrence-month-select" />}
        {["yearly", "once", "every_n_days"].includes(r.type) && <Num label="Day" value={r.day} onChange={set("day")} testid="recurrence-day-input" />}
        {["once", "every_n_days"].includes(r.type) && <Num label={r.type === "once" ? "Year" : "Starting year"} value={r.year} onChange={set("year")} testid="recurrence-year-input" />}
        {r.type === "every_n_days" && <Num label="Every N days" value={r.interval} onChange={set("interval")} testid="recurrence-interval-input" />}
        {r.type === "nth_weekday" && (
          <>
            <Pick label="Which" value={r.nth} onChange={(v) => set("nth")(parseInt(v, 10))} options={NTH_OPTIONS} testid="recurrence-nth-select" />
            <Pick label="Weekday" value={r.weekday} onChange={(v) => set("weekday")(parseInt(v, 10))} options={calendar.weekday_names.map((n, i) => [i, n])} testid="recurrence-weekday-select" />
          </>
        )}
        {r.type === "moon_phase" && (
          <>
            <Pick label="Moon" value={r.moon} onChange={(v) => set("moon")(parseInt(v, 10))} options={calendar.moons.map((m, i) => [i, m.name])} testid="recurrence-moon-select" />
            <Pick label="Phase" value={r.phase} onChange={set("phase")} options={[["full", "Full moon"], ["new", "New moon"]]} testid="recurrence-phase-select" />
          </>
        )}
        {["yearly", "nth_weekday"].includes(r.type) && <Num label="First year (optional)" value={r.start_year} onChange={set("start_year")} placeholder="e.g. birth year" testid="recurrence-start-year-input" />}
      </div>
      <p className="text-xs text-[#A8A090]" data-testid="recurrence-summary">{describeRecurrence(r, calendar)}</p>
    </div>
  );
};
